import {
  ArrowRightOnRectangleIcon,
  BoltIcon,
  BookOpenIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  HomeIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import Navbar from '../core/Navbar'
import { AdminGuard } from '../core/components/AdminGuard'
import { useAuthStore } from '../stores/authStore'

const navItems = [
  {
    to: '/admin/app-settings',
    label: 'App Settings',
    icon: Cog6ToothIcon,
  },
  {
    to: '/admin/manga',
    label: 'Manga Titles',
    icon: BookOpenIcon,
  },
  {
    to: '/admin/sources',
    label: 'Sources',
    icon: ServerStackIcon,
  },
  {
    to: '/admin/edge-functions',
    label: 'Edge Functions',
    icon: BoltIcon,
  },
]

interface AdminSidebarProps {
  isCollapsed: boolean
  onToggle: () => void
}

function AdminSidebar({ isCollapsed, onToggle }: AdminSidebarProps) {
  const profile = useAuthStore((s) => s.profile)
  const signOut = useAuthStore((s) => s.signOut)
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth/login')
  }

  return (
    <aside
      className={`flex flex-col border-gray-200 border-r bg-white transition-all duration-200 ${
        isCollapsed ? 'w-14' : 'w-56'
      } h-full`}
    >
      {/* Header */}
      <div
        className={`flex items-center border-gray-100 border-b px-3 py-4 ${
          isCollapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        {!isCollapsed && (
          <div className="min-w-0 flex-1">
            <h1 className="font-bold text-gray-800 text-lg">Admin Panel</h1>
            <p className="truncate text-gray-500 text-xs">
              {profile?.first_name
                ? `${profile.first_name} ${profile.last_name ?? ''}`.trim()
                : 'Admin'}
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="shrink-0 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          {isCollapsed ? (
            <ChevronRightIcon className="h-4 w-4" />
          ) : (
            <ChevronLeftIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            title={isCollapsed ? label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-2 py-2 font-medium text-sm transition-colors ${
                isActive
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${isCollapsed ? 'justify-center' : ''}`
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {!isCollapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom actions */}
      <div className="space-y-1 border-gray-100 border-t px-2 py-3">
        <button
          type="button"
          onClick={() => navigate('/')}
          title={isCollapsed ? 'Home' : undefined}
          className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-gray-600 text-sm transition-colors hover:bg-gray-100 hover:text-gray-900 ${
            isCollapsed ? 'justify-center' : ''
          }`}
        >
          <HomeIcon className="h-5 w-5 shrink-0" />
          {!isCollapsed && <span>Home</span>}
        </button>
        <button
          type="button"
          onClick={handleSignOut}
          title={isCollapsed ? 'Sign out' : undefined}
          className={`flex w-full items-center gap-3 rounded-md border border-gray-200 px-2 py-2 text-gray-600 text-sm transition-colors hover:bg-gray-100 hover:text-gray-900 ${
            isCollapsed ? 'justify-center' : ''
          }`}
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0" />
          {!isCollapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  )
}

function AdminLayoutInner({
  children,
  appName = 'Admin',
}: {
  children: ReactNode
  appName?: string
}) {
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth < 640)

  return (
    <AdminGuard>
      <div className="flex h-dvh flex-col overflow-hidden">
        <Navbar appName={appName} />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <AdminSidebar
            isCollapsed={isCollapsed}
            onToggle={() => setIsCollapsed((v) => !v)}
          />
          <main className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-6">
            {children}
          </main>
        </div>
      </div>
    </AdminGuard>
  )
}

export function AdminLayout({
  children,
  appName,
}: {
  children: ReactNode
  appName?: string
}) {
  // useAdminAuthInit()
  return <AdminLayoutInner appName={appName}>{children}</AdminLayoutInner>
}

export function AdminPublicLayout({
  children,
  appName = 'Admin',
}: {
  children: ReactNode
  appName?: string
}) {
  // useAdminAuthInit()
  return (
    <>
      <Navbar appName={appName} />
      {children}
    </>
  )
}
